import React from 'react';
import { Badge } from '../ui/badge';
import { Printer } from 'lucide-react';

export const FollowUpsTab = ({ fmtDateUS, patientFollowUps, printPatientReport, safeText }) => (
  <>
    <div className="space-y-3" data-testid="followups-tab-body">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => printPatientReport({
            tabKey: 'followups',
            reportTitle: 'Post-Treatment Follow-Ups Report',
            subtitle: '24-hour patient check-ins and their replies',
            itemNoun: 'follow-up',
            items: patientFollowUps,
            headerColor: '#059669',
            columns: [
              { key: 'procedure', label: 'Procedure' },
              { key: 'sent', label: 'Sent' },
              { key: 'channel', label: 'Channel' },
              { key: 'replied', label: 'Reply Date' },
              { key: 'reply', label: "Patient's Reply" },
              { key: 'status', label: 'Status' },
            ],
            rowFor: (f) => {
              const statusBadge =
                f.status === 'responded' ? '<span class="badge badge-green">REPLIED</span>'
                : f.status === 'sent' ? '<span class="badge badge-amber">AWAITING</span>'
                : f.status === 'failed' ? '<span class="badge badge-red">FAILED</span>'
                : '<span class="badge badge-gray">SCHEDULED</span>';
              const channels = [];
              if (f.emailSent || f.emailSentAt) channels.push('Email');
              if (f.smsSent || f.smsSentAt) channels.push('SMS');
              return [
                safeText(f.procedureName || '—'),
                f.sentAt ? fmtDateUS(f.sentAt) : (f.sendAt ? fmtDateUS(f.sendAt) : '—'),
                channels.join(', ') || '—',
                f.respondedAt ? fmtDateUS(f.respondedAt) : '—',
                f.patientReply ? `<em>${safeText(f.patientReply)}</em>` : '—',
                statusBadge,
              ];
            },
            stats: (items) => [
              { label: 'Total Follow-Ups', value: items.length },
              { label: 'Replied', value: items.filter((x) => x.status === 'responded').length, color: '#059669' },
              { label: 'Awaiting Reply', value: items.filter((x) => x.status === 'sent').length, color: '#d97706' },
            ],
          })}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-md"
          data-testid="followups-print-report-btn"
        >
          <Printer className="h-3.5 w-3.5" />
          Print Follow-Ups Report
        </button>
      </div>
      {patientFollowUps.map((f) => {
        const sendAt = f.sendAt ? new Date(f.sendAt) : null;
        const respondedAt = f.respondedAt ? new Date(f.respondedAt) : null;
        const statusLabel = (
          f.status === 'responded' ? 'Patient replied' :
          f.status === 'sent' ? 'Sent · awaiting reply' :
          f.status === 'pending' ? 'Scheduled' :
          f.status === 'failed' ? 'Send failed' : f.status
        );
        const color =
          f.status === 'responded' ? 'emerald' :
          f.status === 'sent' ? 'blue' :
          f.status === 'pending' ? 'gray' :
          f.status === 'failed' ? 'red' : 'gray';
        return (
          <div
            key={f.id}
            className={`p-4 bg-${color}-50 border border-${color}-200 rounded-lg`}
            data-testid={`followup-record-${f.id}`}
          >
            <div className="flex justify-between items-start">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900">
                  Check-in: {f.procedureName || 'Post-Treatment'}
                </p>
                <p className="text-xs text-gray-600">
                  {f.dentistName} · Scheduled {sendAt ? sendAt.toLocaleString() : '—'}
                  {respondedAt && ` · Replied ${respondedAt.toLocaleString()}`}
                </p>
                {f.responseMessage && (
                  <div className="mt-2 p-3 bg-white border-l-4 border-emerald-500 rounded">
                    <p className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold mb-1">
                      Patient said
                    </p>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{f.responseMessage}</p>
                  </div>
                )}
              </div>
              <Badge variant="outline" className={`bg-${color}-100 text-${color}-800 border-${color}-300 flex-shrink-0 ml-3`}>
                {statusLabel}
              </Badge>
            </div>
          </div>
        );
      })}
    </div>
  </>
);
